import { useRef } from 'react'
import { useFormikContext } from 'formik'

import { EMPTY_OBJECT } from '../../../../../constants/common'
import { EVENT_KEY_ESCAPE } from '../../../../../constants/event'
import { SIZE } from '../../../../../constants/size'
import { useKeyboardListener } from '../../../../../hooks'
import {
  FormBody,
  FormFieldsContainer,
  FormInlineNotifications,
  useFormValidationErrorsNotification,
} from '../../../../Form'
import { Content } from '../../../../Modal'
import {
  APPLICANT_FORM_FIELD_NAME_HOUSEHOLD_SIZE,
  HouseholdSizeField,
  MoveInField,
} from '../../../../RentalPortalApplicant'
import {
  APPLICANT_FORM_FIELD_NAME_EMAIL,
  APPLICANT_FORM_FIELD_NAME_FIRST_NAME,
  APPLICANT_FORM_FIELD_NAME_LAST_NAME,
  APPLICANT_FORM_FIELD_NAME_LISTING,
} from '../RentalPortalApplicantForm.constants'
import { useApplicantFormErrorFocus } from '../RentalPortalApplicantForm.hooks'
import { getHasApplicantFormOpenDropdown } from '../RentalPortalApplicantForm.utils'

import { EmailField } from './EmailField'
import { FirstNameField } from './FirstNameField'
import { LastNameField } from './LastNameField'
import { ListingField } from './ListingField'
import { MessageField } from './MessageField'
import { PhoneField } from './PhoneField'

import * as styles from './RentalPortalApplicantFormFields.module.css'

function RentalPortalApplicantFormFields({
  ContentComponent = Content,
  contentComponentProps: {
    size: contentSize = SIZE.X_LARGE,
    ...contentComponentProps
  } = EMPTY_OBJECT,
  isMinimized,
  notifications,
  onCancel,
  onRemoveNotification,
  onSetNotification,
}) {
  const { dirty, isSubmitting } = useFormikContext()

  const emailRef = useRef(null)
  const firstNameRef = useRef(null)
  const householdSizeRef = useRef(null)
  const lastNameRef = useRef(null)
  const listingRef = useRef(null)

  useFormValidationErrorsNotification({
    onRemoveNotification,
    onSetNotification,
  })

  useApplicantFormErrorFocus({
    [APPLICANT_FORM_FIELD_NAME_EMAIL]: emailRef,
    [APPLICANT_FORM_FIELD_NAME_FIRST_NAME]: firstNameRef,
    [APPLICANT_FORM_FIELD_NAME_HOUSEHOLD_SIZE]: householdSizeRef,
    [APPLICANT_FORM_FIELD_NAME_LAST_NAME]: lastNameRef,
    [APPLICANT_FORM_FIELD_NAME_LISTING]: listingRef,
  })

  function handleCancel() {
    onCancel({ dirty })
  }

  function handleEscape() {
    if (!isMinimized && !getHasApplicantFormOpenDropdown()) {
      handleCancel()
    }
  }

  useKeyboardListener({
    callback: handleEscape,
    dependencies: [dirty, isMinimized, onCancel],
    eventKey: EVENT_KEY_ESCAPE,
  })

  return (
    <FormBody
      actionsProps={{
        ButtonAdd: false,
        ButtonClear: false,
        UploadComponent: false,
        cancelProps: { onClick: handleCancel },
        className: styles.actions,
        isSubmitting,
        submitProps: { isLoading: isSubmitting },
      }}
    >
      <ContentComponent {...contentComponentProps} size={contentSize}>
        <FormInlineNotifications notifications={notifications} />
        <FormFieldsContainer className={styles.fieldsContainer}>
          <FirstNameField fieldRef={firstNameRef} />
          <LastNameField fieldRef={lastNameRef} />
          <EmailField fieldRef={emailRef} />
          <PhoneField />
          <ListingField fieldRef={listingRef} />
          <MoveInField />
          <HouseholdSizeField fieldRef={householdSizeRef} />
          <MessageField />
        </FormFieldsContainer>
      </ContentComponent>
    </FormBody>
  )
}

export default RentalPortalApplicantFormFields
